import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SecretReveal } from '@/components/SecretReveal';

/**
 * Name of the environment variable the node agent reads its API token from
 */
export const AGENT_TOKEN_ENV = 'AGENT_API_TOKEN';

export interface AgentTokenSectionProps {
  isEditMode: boolean;
  /** Whether the node already has a token configured (edit mode) */
  hasAgentToken: boolean;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  /** Rotate handler; when omitted (non-admin) the rotate action is hidden */
  onRotate?: () => void;
  isRotating?: boolean;
  /** Token returned by the last rotation - shown once, until the modal closes */
  rotatedToken?: string | null;
}

/**
 * Agent API token settings of a node: optional token override and the
 * admin-only "Rotate token" action (contract: AGENT_API_TOKEN on the node).
 */
export function AgentTokenSection({
  isEditMode,
  hasAgentToken,
  value,
  onChange,
  error,
  disabled = false,
  onRotate,
  isRotating = false,
  rotatedToken,
}: AgentTokenSectionProps) {
  const helperText = isEditMode
    ? `Currently ${hasAgentToken ? 'configured' : 'not configured'}. Enter a value to replace it, or leave empty to keep it.`
    : 'Optional. Leave empty to let the server generate a secure token.';

  return (
    <div className="node-modal-agent-section">
      <div className="node-modal-section-title">
        Agent API
        {isEditMode && (
          <span
            className={`node-modal-token-status ${hasAgentToken ? 'node-modal-token-status-ok' : 'node-modal-token-status-missing'}`}
          >
            {hasAgentToken ? 'Token configured' : 'Token not configured'}
          </span>
        )}
      </div>

      <Input
        label="Agent API token"
        type="password"
        placeholder={isEditMode ? '(unchanged)' : '(generate automatically)'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        disabled={disabled}
        autoComplete="new-password"
        helperText={helperText}
      />

      {isEditMode && onRotate && (
        <div className="node-modal-token-actions">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onRotate}
            isLoading={isRotating}
            disabled={disabled}
          >
            Rotate token
          </Button>
          <span className="node-modal-token-hint">
            Generates a new token; the old one stops working immediately.
          </span>
        </div>
      )}

      {rotatedToken && (
        <SecretReveal
          value={rotatedToken}
          label={
            <>
              New agent token - it will only be shown once. Set it as{' '}
              <code>{AGENT_TOKEN_ENV}</code> in the node agent environment and restart the agent.
            </>
          }
        />
      )}
    </div>
  );
}
